import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/success-stories";
const TITLE = "Expat Success Stories 2026 | LifeWestWay";

export const Route = createFileRoute("/success-stories")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("Real relocation success stories from the USA, UK, Australia, Italy, Portugal and Canada — visas approved, jobs landed and lives rebuilt abroad in 2026."),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("First-hand relocation journeys: how real applicants secured visas, jobs and permanent residency across six Western destinations."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: SuccessStoriesHub,
});

type Story = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  href: string;
  status: "live" | "soon";
};

const STORIES: Story[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "From F-1 Student to H-1B Engineer in the US (2026 Case Study)",
    blurb:
      "How a student funded $52,000 tuition, built US credit from zero, won the H-1B lottery on OPT and skipped the $100,000 fee.",
    highlight: "OPT → H-1B, no $100k fee",
    href: "/usa/real-success-story-f1-student-to-h1b-engineer-usa-2026",
    status: "live",
  },
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "Two More US Immigrant Journeys: L-1 Family & O-1 Founder (2026)",
    blurb:
      "An L-1 intracompany transfer family in Chicago and a solo O-1 biotech founder — real costs, timelines, and lessons, plus a 2026 FAQ.",
    highlight: "L-1 & O-1 + FAQ",
    href: "/usa/more-real-stories-two-us-immigrant-journeys-faq-2026",
    status: "live",
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "Two More UK Immigrant Journeys: Graduate Route & NHS Nurse (2026)",
    blurb:
      "A finance graduate switching from the Graduate Route to Skilled Worker sponsorship, and an NHS nurse reaching ILR — plus a 2026 UK FAQ.",
    highlight: "Graduate Route & ILR + FAQ",
    href: "/uk/more-real-stories-two-uk-immigrant-journeys-faq-2026",
    status: "live",
  },

  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "Two Australia Immigrant Journeys: Working Holiday & Points Test (2026)",
    blurb:
      "A Working Holiday visa that turned into 482 sponsorship, and a civil engineer's 18-month climb to a Subclass 190 invitation — plus a 2026 FAQ.",
    highlight: "417 → 482 & Subclass 190 + FAQ",
    href: "/australia/more-real-stories-two-australia-immigrant-journeys-faq-2026",
    status: "live",
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-400/10",
    title: "Two Italy Immigrant Journeys: Elective Residency & Investor Visa (2026)",
    blurb:
      "A retired US couple settling near Lecce on the Elective Residency Visa, and a founder using the €250,000 Investor Visa route — plus a 2026 Italy FAQ.",
    highlight: "Elective Residency & Investor Visa + FAQ",
    href: "/italy/more-real-stories-two-italy-immigrant-journeys-faq-2026",
    status: "live",
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "Two Portugal Immigrant Journeys: D8 Freelancer & Golden Visa (2026)",
    blurb:
      "A freelance designer qualifying for the D8 Digital Nomad Visa in Porto, and a family's €500,000 Golden Visa fund route — plus a 2026 Portugal FAQ.",
    highlight: "D8 & Golden Visa + FAQ",
    href: "/portugal/more-real-stories-two-portugal-immigrant-journeys-faq-2026",
    status: "live",
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "Two Canada Immigrant Journeys: PNP Trades & GIC Student Route (2026)",
    blurb:
      "A Ghanaian welder nominated by Saskatchewan for 600 CRS points, and a student's GIC-funded study permit to PGWP path — plus a 2026 Canada FAQ.",
    highlight: "PNP & GIC + FAQ",
    href: "/canada/more-real-stories-two-canada-immigrant-journeys-faq-2026",
    status: "live",
  },
];

function SuccessStoriesHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[{ label: "Home", href: "/" }, { label: "Real Success Story" }]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            Verified journeys · 6 Countries
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Real <span className="text-accent">Success Stories</span> from Expats Abroad
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            First-hand relocation journeys — visas approved, jobs landed and permanent residency
            secured across the USA, UK, Australia, Italy, Portugal and Canada.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section
        aria-label="Real success stories by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {STORIES.map((s) => (
          <Link title={`Read the success story: ${s.title}`}
            key={s.title}
            to={s.href}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${s.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {s.flag}
                </span>
                {s.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                {s.status === "live" ? "2026" : "Soon"}
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {s.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{s.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {s.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Read story <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["success-stories"]} />

      <ShareBar />
    </main>
  );
}
